// Country dial-code list for PhoneInput. Flag is the Unicode emoji
// (two regional-indicator code points) so it renders cross-platform without
// an SVG sprite. Sorted: TH first (default shop locale), then alphabetical.
//
// Keep the list pragmatic — the operator's customers come from a known set
// of countries. Adding entries here is cheap; just keep the dial codes
// correct.

export const COUNTRIES = [
  { code: 'TH', dial: '+66',   flag: '🇹🇭', name: 'Thailand' },
  { code: 'AU', dial: '+61',   flag: '🇦🇺', name: 'Australia' },
  { code: 'CA', dial: '+1',    flag: '🇨🇦', name: 'Canada' },
  { code: 'CN', dial: '+86',   flag: '🇨🇳', name: 'China' },
  { code: 'DE', dial: '+49',   flag: '🇩🇪', name: 'Germany' },
  { code: 'ES', dial: '+34',   flag: '🇪🇸', name: 'Spain' },
  { code: 'FI', dial: '+358',  flag: '🇫🇮', name: 'Finland' },
  { code: 'FR', dial: '+33',   flag: '🇫🇷', name: 'France' },
  { code: 'GB', dial: '+44',   flag: '🇬🇧', name: 'United Kingdom' },
  { code: 'HK', dial: '+852',  flag: '🇭🇰', name: 'Hong Kong' },
  { code: 'ID', dial: '+62',   flag: '🇮🇩', name: 'Indonesia' },
  { code: 'IE', dial: '+353',  flag: '🇮🇪', name: 'Ireland' },
  { code: 'IL', dial: '+972',  flag: '🇮🇱', name: 'Israel' },
  { code: 'IN', dial: '+91',   flag: '🇮🇳', name: 'India' },
  { code: 'IT', dial: '+39',   flag: '🇮🇹', name: 'Italy' },
  { code: 'JP', dial: '+81',   flag: '🇯🇵', name: 'Japan' },
  { code: 'KH', dial: '+855',  flag: '🇰🇭', name: 'Cambodia' },
  { code: 'KR', dial: '+82',   flag: '🇰🇷', name: 'South Korea' },
  { code: 'KZ', dial: '+7',    flag: '🇰🇿', name: 'Kazakhstan' },
  { code: 'LA', dial: '+856',  flag: '🇱🇦', name: 'Laos' },
  { code: 'MM', dial: '+95',   flag: '🇲🇲', name: 'Myanmar' },
  { code: 'MY', dial: '+60',   flag: '🇲🇾', name: 'Malaysia' },
  { code: 'NL', dial: '+31',   flag: '🇳🇱', name: 'Netherlands' },
  { code: 'NO', dial: '+47',   flag: '🇳🇴', name: 'Norway' },
  { code: 'NZ', dial: '+64',   flag: '🇳🇿', name: 'New Zealand' },
  { code: 'PH', dial: '+63',   flag: '🇵🇭', name: 'Philippines' },
  { code: 'PL', dial: '+48',   flag: '🇵🇱', name: 'Poland' },
  { code: 'RU', dial: '+7',    flag: '🇷🇺', name: 'Russia' },
  { code: 'SE', dial: '+46',   flag: '🇸🇪', name: 'Sweden' },
  { code: 'SG', dial: '+65',   flag: '🇸🇬', name: 'Singapore' },
  { code: 'UA', dial: '+380',  flag: '🇺🇦', name: 'Ukraine' },
  { code: 'US', dial: '+1',    flag: '🇺🇸', name: 'United States' },
  { code: 'VN', dial: '+84',   flag: '🇻🇳', name: 'Vietnam' },
  { code: 'ZA', dial: '+27',   flag: '🇿🇦', name: 'South Africa' },
]

export const DEFAULT_COUNTRY = 'TH'

// Country lookups indexed by code and dial.
const BY_CODE = Object.fromEntries(COUNTRIES.map(c => [ c.code, c ]))

export function findByCode(code) {
  return BY_CODE[code] || BY_CODE[DEFAULT_COUNTRY]
}

// Given a stored phone string like "+66 0633796819" or "0633796819",
// return { country, national }. Longest-dial-prefix wins so that ambiguous
// codes (e.g. "+1" used by both US and Canada — we resolve to US by sort
// order) still parse cleanly into a sensible default.
export function parsePhone(value) {
  const raw = (value || '').trim()
  if (raw.startsWith('+')) {
    // Sort dial codes longest first so "+852" doesn't match "+8" etc.
    const sorted = [ ...COUNTRIES ].sort((a, b) => b.dial.length - a.dial.length)
    for (const c of sorted) {
      if (raw.startsWith(c.dial)) {
        return { country: c, national: raw.slice(c.dial.length).trimStart() }
      }
    }
  }
  return { country: findByCode(DEFAULT_COUNTRY), national: raw }
}

// Compose a stored value from a country code + national portion. Returns
// the empty string when both are blank so the field stays nil-friendly.
export function composePhone(country, national) {
  const n = (national || '').trim()
  if (!n && !country) return ''
  const dial = country?.dial ?? ''
  return `${dial} ${n}`.trim()
}
